'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { TradeDirectionMarker } from '@/components/market/identity/trade-direction-marker';
import { UserIdentity } from '@/components/profile/user-identity';
import { ThesisActions } from '@/components/social/debate/thesis-actions';
import { ThesisMarketAvatar } from '@/components/social/debate/thesis-market-avatar';
import { ThesisReplyThread } from '@/components/social/debate/thesis-reply-thread';
import { READ_ONLY_THESIS_REPLY_WRITE_ACCESS, type ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { SolscanLink } from '@/components/social/shared/solscan-link';
import type { ThesisItem } from '@/components/social/types';
import { resolveProposalCategory } from '@/config/proposal-display';
import { useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent, useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FALLBACK_CATEGORY_EMOJI = '\u{1F4A1}';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Variant = 'debate' | 'portfolio';
interface ThesisCardProps {
  thesis: ThesisItem;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level access controls writing only; threads stay public. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /**
   * Discriminator between the two surfaces (spec 094):
   *
   * - `'debate'` (default) — rendered inside the market-detail Debate tab.
   *   Profile-picture avatar that routes to `/profile/{userId}`. The card body
   *   is not pressable; the viewer is already on the parent market. Replies
   *   expand inline.
   * - `'portfolio'` — rendered inside the portfolio Theses panel. Rounded
   *   card surface with an absolute-positioned `<Link>` overlay to the
   *   market's Debate tab anchored to this thesis. Avatar is a rounded-square
   *   market image with an outcome-colored status dot. Parent market title
   *   above each card uses the same typography as portfolio PositionRow market titles.
   *   Replies do not expand inline (the viewer is one tap away from the destination's full thread).
   */
  variant?: Variant;
  /** Cross-market feeds need a visible destination even with the debate presentation. */
  showMarketLink?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisCard(t0) {
  const $ = _c(123);
  const {
    thesis,
    isAuthenticated,
    userId,
    replyWriteAccess: t1,
    onOpenTradeForm,
    variant: t2,
    showMarketLink: t3
  } = t0;
  const replyWriteAccess = t1 === undefined ? READ_ONLY_THESIS_REPLY_WRITE_ACCESS : t1;
  const variant = t2 === undefined ? "debate" : t2;
  const showMarketLink = t3 === undefined ? false : t3;
  const locale = useLocale();
  const [showReplies, setShowReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(thesis.replyCount);
  const tTimeIntl = useTranslations("time");
  const tDebate = useTranslations("social.debate");
  const {
    formatD6
  } = useFormattedCurrency();
  let t4;
  if ($[0] !== tTimeIntl) {
    t4 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const tTime = t4;
  const outcomeColor = useMarketOutcomeColor(thesis.marketPubkey, thesis.outcomeIndex);
  const hue = outcomeColor.hue;
  const isBuy = thesis.tradeDirection === "buy";
  let t5;
  if ($[2] !== isBuy || $[3] !== tDebate) {
    t5 = isBuy ? tDebate("bought") : tDebate("sold");
    $[2] = isBuy;
    $[3] = tDebate;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const verb = t5;
  const outcomeLabel = thesis.outcomeLabel ?? "";
  let t6;
  if ($[5] !== formatD6 || $[6] !== thesis.tradeAmount) {
    t6 = formatD6(thesis.tradeAmount);
    $[5] = formatD6;
    $[6] = thesis.tradeAmount;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const tradeLineText = `${verb} ${outcomeLabel} for ${t6}`;
  const traderHref = `/portfolio/${thesis.trader.userId ?? ""}`;
  const marketHref = `/markets/${thesis.marketPubkey}`;
  const deepLinkHref = `/markets/${thesis.marketPubkey}?tab=debate&thesis=${thesis.signature}`;
  const traderIntent = useUserProfilePrefetchIntent(thesis.trader.userId);
  const marketIntent = useMarketDetailPrefetchIntent(thesis.marketPubkey);
  const deepLinkIntent = useIntentRoutePrefetch(deepLinkHref, marketIntent.onMouseEnter);
  const categoryEmoji = thesis.marketCategory.length > 0 ? resolveProposalCategory(thesis.marketCategory)?.emoji ?? FALLBACK_CATEGORY_EMOJI : FALLBACK_CATEGORY_EMOJI;
  let t7;
  if ($[8] !== replyWriteAccess || $[9] !== showReplies) {
    t7 = function handleToggleReplies() {
      if (!showReplies && replyWriteAccess.status === "ineligible") {
        replyWriteAccess.refresh();
      }
      setShowReplies(_temp);
    };
    $[8] = replyWriteAccess;
    $[9] = showReplies;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleToggleReplies = t7;
  let t8;
  if ($[11] !== thesis.trader) {
    t8 = <UserIdentity user={thesis.trader} size="md" nameClassName="text-foreground-secondary" handleClassName="text-body text-foreground-tertiary" />;
    $[11] = thesis.trader;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8 || $[14] !== traderHref || $[15] !== traderIntent) {
    t9 = <Link href={traderHref} prefetch={false} className="relative z-10 min-w-0 truncate hover:underline" {...traderIntent} onClick={_temp2}>{t8}</Link>;
    $[13] = t8;
    $[14] = traderHref;
    $[15] = traderIntent;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== locale || $[19] !== tTime || $[20] !== thesis.createdAt) {
    t11 = formatRelativeTime(thesis.createdAt, tTime, {
      locale
    });
    $[18] = locale;
    $[19] = tTime;
    $[20] = thesis.createdAt;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <span className="shrink-0 text-body text-foreground-tertiary">{t11}</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== thesis.signature) {
    t14 = <SolscanLink signature={thesis.signature} variant="inline" className="relative z-10" />;
    $[25] = thesis.signature;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14 || $[29] !== t9) {
    t15 = <div className="flex flex-wrap items-center gap-1">{t9}{t10}{t12}{t13}{t14}</div>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const metadataRow = t15;
  let t16;
  if ($[31] !== outcomeColor.text) {
    t16 = cn("text-body tabular-nums", outcomeColor.text);
    $[31] = outcomeColor.text;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16 || $[34] !== tradeLineText) {
    t17 = <p className={t16}>{tradeLineText}</p>;
    $[33] = t16;
    $[34] = tradeLineText;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const tradeLine = t17;
  let t18;
  if ($[36] !== thesis.content) {
    t18 = <p className="text-body leading-snug">{thesis.content}</p>;
    $[36] = thesis.content;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const body = t18;
  if (variant === "debate") {
    const t19 = `thesis-${thesis.signature}`;
    const t20 = thesis.trader.avatarNsfw ?? false;
    const t21 = thesis.trader.avatarPreferences?.rasterise ?? false;
    let t22;
    if ($[38] !== t20 || $[39] !== t21 || $[40] !== thesis.trader.avatarUrl || $[41] !== thesis.trader.displayName || $[42] !== thesis.trader.userId) {
      t22 = <UserAvatar userId={thesis.trader.userId} displayName={thesis.trader.displayName} avatarUrl={thesis.trader.avatarUrl} nsfw={t20} rasterise={t21} size="md" />;
      $[38] = t20;
      $[39] = t21;
      $[40] = thesis.trader.avatarUrl;
      $[41] = thesis.trader.displayName;
      $[42] = thesis.trader.userId;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    let t23;
    if ($[44] !== thesis.tradeDirection) {
      t23 = <TradeDirectionMarker direction={thesis.tradeDirection} />;
      $[44] = thesis.tradeDirection;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    let t24;
    if ($[46] !== t22 || $[47] !== t23) {
      t24 = <span className="relative block size-10">{t22}{t23}</span>;
      $[46] = t22;
      $[47] = t23;
      $[48] = t24;
    } else {
      t24 = $[48];
    }
    let t25;
    if ($[49] !== t24 || $[50] !== traderHref || $[51] !== traderIntent) {
      t25 = <Link href={traderHref} prefetch={false} className="shrink-0" {...traderIntent}>{t24}</Link>;
      $[49] = t24;
      $[50] = traderHref;
      $[51] = traderIntent;
      $[52] = t25;
    } else {
      t25 = $[52];
    }
    let t26;
    if ($[53] !== deepLinkHref || $[54] !== deepLinkIntent || $[55] !== showMarketLink || $[56] !== thesis.marketTitle) {
      t26 = showMarketLink ? <Link href={deepLinkHref} prefetch={false} className="truncate text-body font-medium text-foreground-primary hover:underline" title={thesis.marketTitle} {...deepLinkIntent}>{thesis.marketTitle}</Link> : null;
      $[53] = deepLinkHref;
      $[54] = deepLinkIntent;
      $[55] = showMarketLink;
      $[56] = thesis.marketTitle;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    let t27;
    if ($[58] !== body || $[59] !== metadataRow || $[60] !== t26 || $[61] !== tradeLine) {
      t27 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t26}{metadataRow}{tradeLine}{body}</div>;
      $[58] = body;
      $[59] = metadataRow;
      $[60] = t26;
      $[61] = tradeLine;
      $[62] = t27;
    } else {
      t27 = $[62];
    }
    let t28;
    if ($[63] !== t25 || $[64] !== t27) {
      t28 = <div className="flex gap-3">{t25}{t27}</div>;
      $[63] = t25;
      $[64] = t27;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    let t29;
    if ($[66] !== handleToggleReplies || $[67] !== isAuthenticated || $[68] !== replyCount || $[69] !== thesis) {
      t29 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} onToggleReplies={handleToggleReplies} /></div>;
      $[66] = handleToggleReplies;
      $[67] = isAuthenticated;
      $[68] = replyCount;
      $[69] = thesis;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    let t30;
    if ($[71] !== isAuthenticated || $[72] !== onOpenTradeForm || $[73] !== replyWriteAccess || $[74] !== showReplies || $[75] !== thesis.marketPubkey || $[76] !== thesis.signature || $[77] !== userId) {
      t30 = showReplies ? <div className="contents md:block md:pl-[52px]"><ThesisReplyThread thesisSignature={thesis.signature} marketPubkey={thesis.marketPubkey} isAuthenticated={isAuthenticated} userId={userId} writeAccess={replyWriteAccess} onClose={() => setShowReplies(false)} {...onOpenTradeForm !== undefined ? {
          onOpenTradeForm
        } : {}} onReplyAdded={() => setReplyCount(_temp3)} onReplyRemoved={() => setReplyCount(_temp4)} /></div> : null;
      $[71] = isAuthenticated;
      $[72] = onOpenTradeForm;
      $[73] = replyWriteAccess;
      $[74] = showReplies;
      $[75] = thesis.marketPubkey;
      $[76] = thesis.signature;
      $[77] = userId;
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    let t31;
    if ($[79] !== t19 || $[80] !== t28 || $[81] !== t29 || $[82] !== t30) {
      t31 = <div id={t19} className="flex flex-col gap-2 px-0 pt-3 pb-2">{t28}{t29}{t30}</div>;
      $[79] = t19;
      $[80] = t28;
      $[81] = t29;
      $[82] = t30;
      $[83] = t31;
    } else {
      t31 = $[83];
    }
    return t31;
  }
  const t19 = `thesis-${thesis.signature}`;
  let t20;
  if ($[84] !== deepLinkHref || $[85] !== deepLinkIntent || $[86] !== thesis.marketTitle) {
    t20 = <Link href={deepLinkHref} prefetch={false} aria-label={thesis.marketTitle} className="absolute inset-0 z-0 rounded-panel-row focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" {...deepLinkIntent} />;
    $[84] = deepLinkHref;
    $[85] = deepLinkIntent;
    $[86] = thesis.marketTitle;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== thesis.marketTitle) {
    t21 = <p className="truncate text-body font-medium text-foreground-primary" title={thesis.marketTitle}>{thesis.marketTitle}</p>;
    $[88] = thesis.marketTitle;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  let t22;
  if ($[90] !== categoryEmoji || $[91] !== hue || $[92] !== outcomeLabel || $[93] !== thesis.marketPubkey || $[94] !== thesis.outcomeImageNsfw || $[95] !== thesis.outcomeImageUrl || $[96] !== thesis.outcomeIndex) {
    t22 = <ThesisMarketAvatar marketPubkey={thesis.marketPubkey} outcomeIndex={thesis.outcomeIndex} outcomeTitle={outcomeLabel} outcomeImageUrl={thesis.outcomeImageUrl} outcomeImageNsfw={thesis.outcomeImageNsfw} categoryEmoji={categoryEmoji} hue={hue} size="md" />;
    $[90] = categoryEmoji;
    $[91] = hue;
    $[92] = outcomeLabel;
    $[93] = thesis.marketPubkey;
    $[94] = thesis.outcomeImageNsfw;
    $[95] = thesis.outcomeImageUrl;
    $[96] = thesis.outcomeIndex;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  let t23;
  if ($[98] !== marketHref || $[99] !== marketIntent || $[100] !== t22) {
    t23 = <Link href={marketHref} prefetch={false} className="relative z-10 shrink-0" {...marketIntent} onClick={_temp5}>{t22}</Link>;
    $[98] = marketHref;
    $[99] = marketIntent;
    $[100] = t22;
    $[101] = t23;
  } else {
    t23 = $[101];
  }
  let t24;
  if ($[102] !== body || $[103] !== metadataRow || $[104] !== tradeLine) {
    t24 = <div className="flex min-w-0 flex-1 flex-col gap-1">{tradeLine}{metadataRow}{body}</div>;
    $[102] = body;
    $[103] = metadataRow;
    $[104] = tradeLine;
    $[105] = t24;
  } else {
    t24 = $[105];
  }
  let t25;
  if ($[106] !== t23 || $[107] !== t24) {
    t25 = <div className="flex gap-3">{t23}{t24}</div>;
    $[106] = t23;
    $[107] = t24;
    $[108] = t25;
  } else {
    t25 = $[108];
  }
  let t26;
  if ($[109] !== isAuthenticated || $[110] !== replyCount || $[111] !== thesis) {
    t26 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} mode="display" /></div>;
    $[109] = isAuthenticated;
    $[110] = replyCount;
    $[111] = thesis;
    $[112] = t26;
  } else {
    t26 = $[112];
  }
  let t27;
  if ($[113] !== t25 || $[114] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t25}{t26}</div>;
    $[113] = t25;
    $[114] = t26;
    $[115] = t27;
  } else {
    t27 = $[115];
  }
  let t28;
  if ($[116] !== t21 || $[117] !== t27) {
    t28 = <div className="flex flex-col gap-3">{t21}{t27}</div>;
    $[116] = t21;
    $[117] = t27;
    $[118] = t28;
  } else {
    t28 = $[118];
  }
  let t29;
  if ($[119] !== t19 || $[120] !== t20 || $[121] !== t28) {
    t29 = <div id={t19} className="relative rounded-panel-row border border-border-secondary p-3 transition-colors hover:bg-background-tertiary/80">{t20}{t28}</div>;
    $[119] = t19;
    $[120] = t20;
    $[121] = t28;
    $[122] = t29;
  } else {
    t29 = $[122];
  }
  return t29;
}
function _temp5(e_0) {
  e_0.stopPropagation();
}
function _temp4(c_0) {
  return Math.max(0, c_0 - 1);
}
function _temp3(c) {
  return c + 1;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(visible) {
  return !visible;
}